import React, { useCallback, useMemo } from 'react';
import { ElementSelectPropertyCountType } from '@shared/models/element-property.model';
import { TypographyProps } from '@mui/material';
import { createPropertySelectOptions } from '@shared/helpers/element-property-factory.helpers';
import { getSbefPropertyOptions } from '@shared/templates/categories/common-element-category-properties.helpers';
import { PropertyMenu } from '@ui/menus/PropertyMenu';
import { SbefCode } from '@shared/construction/sbef.model';
import { getSbefSelectorLabel, isSbefCode } from '@shared/helpers/sbef.helpers';
import { ELEMENT_PROPERTY_SELECT_INHERIT_COUNT } from '@shared/constants/expression-variables.constants';

interface RecipeSelectorSBEFMenuProps {
  /**
   * The selected sbef code. Undefined if none is selected.
   */
  sbefCode: SbefCode | undefined;

  /**
   * Callback when selecting an sbef code.
   */
  onSelect: (sbef: SbefCode | undefined) => void;
}

const RecipeSelectorSBEFMenu: React.FC<RecipeSelectorSBEFMenuProps> = ({
  sbefCode,
  onSelect,
}) => {
  const sbefOptions = useMemo(
    () => [
      ...createPropertySelectOptions([ELEMENT_PROPERTY_SELECT_INHERIT_COUNT]),
      ...createPropertySelectOptions(getSbefPropertyOptions()),
    ],
    [],
  );

  const handleSelect = useCallback(
    (value: ElementSelectPropertyCountType) => {
      // Deselect if none is selected
      if (value === 'none') {
        onSelect(undefined);
        return;
      } else if (!isSbefCode(value)) {
        throw new Error('Invalid sbef code: ' + value);
      }
      onSelect(value);
    },
    [onSelect],
  );

  const buttonLabelProps = useMemo<TypographyProps>(
    () => ({
      sx: {
        textTransform: 'uppercase',
        color: 'neutral.main',
        fontSize: 12,
        fontWeight: 500,
        maxWidth: 500,
      },
    }),
    [],
  );

  return (
    <PropertyMenu
      selectedValue={sbefCode}
      selectedLabel={sbefCode ? getSbefSelectorLabel(sbefCode) : 'SBEF Code'}
      options={sbefOptions}
      onSelect={handleSelect}
      buttonLabelProps={buttonLabelProps}
    />
  );
};

export default RecipeSelectorSBEFMenu;
